<template>
  <transition name="aig-fade">
    <div v-if="open" class="aig-overlay" role="dialog" aria-modal="true" aria-labelledby="aig-title" @click.self="cancel">
      <div class="aig-dialog">
        <header class="aig-header">
          <div class="aig-title-wrap">
            <span class="aig-badge">AI</span>
            <div>
              <div id="aig-title" class="aig-title">Generate Practice Test with AI</div>
              <div class="aig-subtitle">
                Set the parameters below and we'll draft a complete assessment ready for review and editing.
              </div>
            </div>
          </div>
          <button class="aig-close" :disabled="generating" title="Close" @click="cancel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="aig-body">
          <div class="aig-field">
            <label class="aig-label" for="aig-name">Practice Test Name</label>
            <input id="aig-name"
                   ref="nameInput"
                   class="aig-input"
                   type="text"
                   v-model="form.name"
                   :disabled="generating"
                   placeholder="e.g. AZ-104 — Identity & Access Mock Exam #1"
                   maxlength="120" />
            <div v-if="errors.name" class="aig-err">{{ errors.name }}</div>
          </div>

          <div class="aig-field-row">
            <div class="aig-field">
              <label class="aig-label" for="aig-count">Number of Questions</label>
              <input id="aig-count"
                     class="aig-input"
                     type="number"
                     min="1"
                     max="100"
                     v-model.number="form.count"
                     :disabled="generating" />
              <div v-if="errors.count" class="aig-err">{{ errors.count }}</div>
              <div v-else class="aig-hint">Between 1 and 100.</div>
            </div>

            <div class="aig-field" style="flex:1">
              <label class="aig-label">Type of Questions</label>
              <div class="aig-seg">
                <button v-for="opt in typeOptions" :key="opt.key"
                        class="aig-seg-btn"
                        :class="{ active: form.type === opt.key }"
                        :disabled="generating"
                        @click="form.type = opt.key">
                  <component :is="opt.icon" />
                  {{ opt.label }}
                </button>
              </div>
              <div class="aig-hint">{{ typeHint }}</div>
            </div>
          </div>

          <!-- Conflict notice when there's existing content -->
          <div v-if="hasExisting && !generating" class="aig-warn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>
              Your current draft has <b>{{ existingCount }} question{{ existingCount === 1 ? '' : 's' }}</b>.
              Generating will replace it.
            </span>
          </div>

          <!-- Generating state -->
          <div v-if="generating" class="aig-progress">
            <div class="aig-spinner-wrap">
              <svg class="aig-spinner" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M21 12a9 9 0 1 1-6.36-8.59"/>
              </svg>
            </div>
            <div class="aig-progress-text">
              <div class="aig-progress-title">{{ progressLabel }}</div>
              <div class="aig-progress-bar">
                <div class="aig-progress-fill" :style="{ width: progressPct + '%' }"></div>
              </div>
            </div>
          </div>
        </div>

        <footer class="aig-footer">
          <button class="btn-ghost" :disabled="generating" @click="cancel">Cancel</button>
          <button class="aig-generate-btn" :disabled="generating" @click="generate">
            <svg v-if="!generating" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polygon points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"/></svg>
            {{ generating ? 'Generating…' : 'Generate' }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { generatePracticeTest } from '../data/aiGenerator.js';

const RadioIcon = {
  render(h) {
    return h('svg', {
      attrs: { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2.5 },
    }, [
      h('circle', { attrs: { cx: 12, cy: 12, r: 9 } }),
      h('circle', { attrs: { cx: 12, cy: 12, r: 4, fill: 'currentColor' } }),
    ]);
  },
};
const CheckIcon = {
  render(h) {
    return h('svg', {
      attrs: { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2.5 },
    }, [
      h('rect', { attrs: { x: 3, y: 3, width: 18, height: 18, rx: 3 } }),
      h('polyline', { attrs: { points: '8 12 11 15 16 9' } }),
    ]);
  },
};
const MixIcon = {
  render(h) {
    return h('svg', {
      attrs: { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2.5, 'stroke-linecap': 'round' },
    }, [
      h('circle', { attrs: { cx: 8, cy: 8, r: 5 } }),
      h('rect', { attrs: { x: 11, y: 11, width: 10, height: 10, rx: 2 } }),
    ]);
  },
};

export default {
  name: 'AiGenerateModal',
  components: { RadioIcon, CheckIcon, MixIcon },
  data() {
    return {
      form: { name: '', count: 10, type: 'mixed' },
      errors: {},
      generating: false,
      progressPct: 0,
      progressLabel: '',
      progressInterval: null,
      typeOptions: [
        { key: 'single', label: 'Single only',  icon: 'RadioIcon' },
        { key: 'multi',  label: 'Multi only',   icon: 'CheckIcon' },
        { key: 'mixed',  label: 'Mixed',        icon: 'MixIcon'   },
      ],
    };
  },
  computed: {
    open() { return this.$store.state.ui.aiGenerateOpen; },
    existingCount() { return this.$store.state.assessment.questions.length; },
    hasExisting() { return this.existingCount > 0; },
    typeHint() {
      if (this.form.type === 'single') return 'Every question has exactly one correct answer.';
      if (this.form.type === 'multi')  return 'Every question requires selecting all correct answers.';
      return 'A blend of single- and multiple-choice questions.';
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.reset();
        document.addEventListener('keydown', this.onKey);
        this.$nextTick(() => { if (this.$refs.nameInput) this.$refs.nameInput.focus(); });
      } else {
        document.removeEventListener('keydown', this.onKey);
        this.stopProgress();
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
    this.stopProgress();
  },
  methods: {
    reset() {
      this.form = { name: '', count: 10, type: 'mixed' };
      this.errors = {};
      this.generating = false;
      this.progressPct = 0;
      this.progressLabel = '';
    },
    onKey(e) {
      if (this.generating) return;
      if (e.key === 'Escape') this.cancel();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.generate();
    },
    cancel() {
      if (this.generating) return;
      this.$store.commit('CLOSE_AI_GENERATE');
    },
    validate() {
      const errs = {};
      if (!this.form.name || !this.form.name.trim()) {
        errs.name = 'A name is required.';
      }
      const n = Number(this.form.count);
      if (!Number.isFinite(n) || n < 1 || n > 100) {
        errs.count = 'Pick a number between 1 and 100.';
      }
      this.errors = errs;
      return Object.keys(errs).length === 0;
    },
    stopProgress() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },
    /**
     * Animate the progress bar across a sequence of labelled steps so the
     * generation feels like a real model run rather than an instant import.
     */
    runProgress(onDone) {
      const steps = [
        { label: 'Analyzing test parameters…',  pct: 18, ms: 350 },
        { label: 'Drafting question prompts…',   pct: 48, ms: 480 },
        { label: 'Generating answer options…',   pct: 76, ms: 420 },
        { label: 'Validating correctness keys…', pct: 94, ms: 320 },
        { label: 'Finalizing assessment…',       pct: 100, ms: 220 },
      ];
      let i = 0;
      const advance = () => {
        if (i >= steps.length) { onDone(); return; }
        const s = steps[i++];
        this.progressLabel = s.label;
        this.progressPct = s.pct;
        this.progressInterval = setTimeout(advance, s.ms);
      };
      advance();
    },
    generate() {
      if (!this.validate()) return;
      this.generating = true;
      this.progressPct = 0;
      this.progressLabel = 'Connecting to model…';

      const params = {
        name: this.form.name.trim(),
        count: Number(this.form.count),
        type: this.form.type,
      };

      this.runProgress(() => {
        try {
          const draft = generatePracticeTest(params);

          // Replace the current draft entirely with the generated one.
          this.$store.commit('RESET_ASSESSMENT');
          this.$store.commit('UPDATE_ASSESSMENT', {
            title: draft.title,
            description: `AI-generated practice test · ${params.count} ${params.type === 'mixed' ? 'mixed' : params.type + '-choice'} questions`,
          });
          this.$store.commit('IMPORT_QUESTIONS', draft.questions);

          this.$store.dispatch('toast', `Generated "${draft.title}" with ${draft.questions.length} question${draft.questions.length === 1 ? '' : 's'}`);
          this.generating = false;
          this.$store.commit('CLOSE_AI_GENERATE');
        } catch (err) {
          this.generating = false;
          this.$store.dispatch('toast', 'Generation failed — please try again');
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    },
  },
};
</script>

<style scoped>
.aig-overlay {
  position: fixed; inset: 0;
  background: rgba(13, 31, 60, 0.55);
  backdrop-filter: blur(2px);
  display: grid; place-items: center;
  z-index: 600; padding: 24px;
}
.aig-dialog {
  background: var(--card);
  border-radius: 14px;
  width: 100%; max-width: 560px;
  max-height: 92vh;
  overflow: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,0.30);
  border: 1px solid var(--border);
}
.aig-header {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 20px 24px 14px;
  border-bottom: 1px solid var(--border-soft);
  background: linear-gradient(180deg, #faf5ff 0%, var(--card) 70%);
}
.aig-title-wrap {
  display: flex; align-items: flex-start; gap: 12px;
  flex: 1; min-width: 0;
}
.aig-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border-radius: 8px;
  font-size: 11px; font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.35);
  margin-top: 2px;
}
.aig-title { font-size: 16px; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; }
.aig-subtitle { font-size: 12.5px; color: var(--muted); line-height: 1.5; margin-top: 4px; max-width: 420px; }

.aig-close {
  margin-left: auto;
  background: none; border: none; padding: 4px;
  border-radius: 6px; color: var(--muted); cursor: pointer; flex-shrink: 0;
}
.aig-close:hover:not(:disabled) { background: var(--bg); color: var(--ink); }
.aig-close:disabled { opacity: 0.4; cursor: not-allowed; }

.aig-body { padding: 18px 24px; }
.aig-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; min-width: 0; }
.aig-field-row { display: flex; gap: 14px; }
.aig-field-row .aig-field { flex: 1; margin-bottom: 14px; }

.aig-label {
  font-size: 11px; font-weight: 700;
  color: var(--muted);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.aig-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  color: var(--ink);
  font-size: 13.5px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.13s, box-shadow 0.13s;
}
.aig-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(0,128,201,0.1); }
.aig-input:disabled { background: var(--bg-alt); opacity: 0.7; cursor: not-allowed; }

.aig-hint { font-size: 11px; color: var(--muted); }
.aig-err { font-size: 11.5px; color: var(--danger); font-weight: 500; }

.aig-seg {
  display: inline-flex;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
  width: 100%;
}
.aig-seg-btn {
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 7px 10px;
  background: transparent;
  color: var(--ink-3);
  border: none;
  border-radius: 6px;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
}
.aig-seg-btn:hover:not(:disabled) { color: var(--ink); }
.aig-seg-btn.active {
  background: var(--card);
  color: var(--accent-dark);
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}
.aig-seg-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.aig-warn {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px;
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12.5px;
  margin-top: 4px;
}

.aig-progress {
  margin-top: 4px;
  padding: 14px 16px;
  background: linear-gradient(180deg, #faf5ff 0%, #f5f3ff 100%);
  border: 1px solid #e9d5ff;
  border-radius: 10px;
  display: flex; align-items: center; gap: 14px;
}
.aig-spinner-wrap {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: grid; place-items: center;
  flex-shrink: 0;
  color: #fff;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.35);
}
.aig-spinner { animation: aig-spin 0.9s linear infinite; }
@keyframes aig-spin { to { transform: rotate(360deg); } }

.aig-progress-text { flex: 1; min-width: 0; }
.aig-progress-title {
  font-size: 13px; font-weight: 600;
  color: #5b21b6;
  margin-bottom: 6px;
}
.aig-progress-bar {
  height: 6px;
  background: rgba(99, 102, 241, 0.15);
  border-radius: 99px;
  overflow: hidden;
}
.aig-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
  border-radius: 99px;
  transition: width 0.35s ease;
}

.aig-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px 18px;
  border-top: 1px solid var(--border-soft);
}
.aig-generate-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  transition: transform 0.13s, box-shadow 0.13s, filter 0.13s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}
.aig-generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.06);
  box-shadow: 0 6px 18px rgba(99, 102, 241, 0.5);
}
.aig-generate-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

.aig-fade-enter-active, .aig-fade-leave-active { transition: opacity 0.18s ease; }
.aig-fade-enter, .aig-fade-leave-to { opacity: 0; }

/* ── Mobile: bottom-sheet style for narrow viewports ──────────────── */
@media (max-width: 540px) {
  .aig-overlay {
    padding: 0;
    align-items: flex-end;
  }
  .aig-dialog {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 14px 14px 0 0;
    /* Honor the iOS home-indicator so the dialog footer isn't covered. */
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
